/**
 * フォーム詳細ページのタブ（ビルダ ↔ 回収）。
 * 両ページのヘッダー直下にマウントし、クリックで navigate する。
 */
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface FormPageTabsProps {
  formId: string;
  submissionCount: number;
  active: 'builder' | 'settings' | 'completion' | 'submissions';
  /**
   * 呼び出し元に未保存の変更があるか (2026-07-28)。
   * ビルダーと設定は同じ forms/{id} を別ページで編集するため、
   * 保存せずにタブを移ると編集内容が捨てられる。移動前に確認を挟む。
   */
  dirty?: boolean;
}

export function FormPageTabs({
  formId,
  submissionCount,
  active,
  dirty = false,
}: FormPageTabsProps) {
  const navigate = useNavigate();

  const go = (to: string) => {
    if (
      dirty &&
      !window.confirm(
        '保存していない変更があります。移動すると変更は破棄されます。よろしいですか？',
      )
    ) {
      return;
    }
    navigate(to);
  };

  const tabClass = (isActive: boolean) =>
    cn(
      'px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors inline-flex items-center gap-2 cursor-pointer',
      isActive
        ? 'text-[var(--accent)] border-[var(--accent)]'
        : 'text-[var(--text-muted)] border-transparent hover:text-[var(--text-primary)]',
    );

  return (
    <div className="flex gap-1 border-b border-[var(--border)] mb-5">
      <button
        type="button"
        className={tabClass(active === 'builder')}
        onClick={() => go(`/forms/${formId}`)}
      >
        ビルダー
      </button>
      <button
        type="button"
        className={tabClass(active === 'settings')}
        onClick={() => go(`/forms/${formId}/settings`)}
      >
        設定
      </button>
      <button
        type="button"
        className={tabClass(active === 'completion')}
        onClick={() => go(`/forms/${formId}/completion`)}
      >
        完了通知
      </button>
      <button
        type="button"
        className={tabClass(active === 'submissions')}
        onClick={() => go(`/forms/${formId}/submissions`)}
      >
        回収
        <Badge variant={active === 'submissions' ? 'accent' : 'default'}>
          {submissionCount}
        </Badge>
      </button>
    </div>
  );
}
